import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { subscribeWidgetPerf, getWidgetPerf, clearWidgetPerf } from "@/lib/diagnostics";
import { Gauge, RefreshCw, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Power BI-style Performance Analyzer: live per-widget query timings for this dashboard.
export function PerfAnalyzerPanel({ open, onOpenChange, dashboardId, onRefreshAll }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    return subscribeWidgetPerf(() => setTick((t) => t + 1));
  }, [open]);

  const rows = getWidgetPerf().filter((r) => r.dashboardId === dashboardId).sort((a, b) => b.apiMs - a.apiMs);

  const maxMs = Math.max(...rows.map((r) => r.apiMs), 1);
  const total = rows.reduce((s, r) => s + r.apiMs, 0);
  const color = (ms) => (ms < 500 ? "var(--success)" : ms < 2000 ? "#F59E0B" : "#DC2626");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-[color:var(--canvas)]" data-testid="perf-analyzer-panel">
        <SheetHeader className="px-5 py-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <SheetTitle className="flex items-center gap-2 font-display">
            <Gauge className="w-5 h-5 text-[color:var(--brand-ocean)]" />Performance analyzer
          </SheetTitle>
        </SheetHeader>
        <div className="px-4 pt-3 flex items-center gap-2">
          <Button data-testid="perf-refresh-all" size="sm" variant="outline" className="bg-[color:var(--surface)]"
            onClick={() => { clearWidgetPerf(dashboardId); onRefreshAll?.(); }}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh & measure
          </Button>
          <Button data-testid="perf-clear" size="sm" variant="ghost" onClick={() => clearWidgetPerf(dashboardId)}>
            <Trash2 className="w-3.5 h-3.5 mr-1" />Clear
          </Button>
          <span className="ml-auto text-[11px] text-[color:var(--ink-3)]">{rows.length} visuals · {(total / 1000).toFixed(1)}s total</span>
        </div>
        <ScrollArea className="flex-1 px-4 py-3">
          {rows.length === 0 && (
            <p className="text-sm text-[color:var(--ink-3)] text-center py-10">
              No measurements yet.<br />Click “Refresh & measure” to time every visual's query.
            </p>
          )}
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.widgetId} data-testid={`perf-row-${r.widgetId}`} className="glass-card p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[color:var(--ink)] flex-1 truncate">{r.title}</span>
                  <span className="text-[10px] text-[color:var(--ink-3)]">{r.type}</span>
                  <span className={cn("text-xs font-bold tabular-nums")} style={{ color: color(r.apiMs) }}>{r.apiMs.toLocaleString()} ms</span>
                </div>
                <div className="h-1.5 mt-1.5 rounded-full bg-[color:var(--glass-border)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(2, (r.apiMs / maxMs) * 100)}%`, background: color(r.apiMs) }} />
                </div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-[color:var(--ink-3)]">
                  <span>{Number(r.rows).toLocaleString()} rows</span>
                  <span>· measured {r.count}×</span>
                  <span className="ml-auto">{new Date(r.at).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>
          {rows.length > 0 && (
            <p className="text-[10px] text-[color:var(--ink-3)] mt-3">
              Timings measure the data query round-trip per visual. Green &lt; 0.5s · amber &lt; 2s · red ≥ 2s. Slow visuals usually benefit from fewer dimensions, filters, or a prepared (materialized) dataset.
            </p>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
